import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import TextInputBox from '@components/TextInputBox';

const messages = defineMessages({
  cancel: {
    id: 'GLOBAL.cancel',
    defaultMessage: 'Cancel',
  },
  confirm: {
    id: 'GLOBAL.confirm',
    defaultMessage: 'Confirm',
  },
});

interface ConfirmationChallenge {
  phrase: string;
  description: React.ReactNode;
  /**
   * Confirmation type
   *
   * @description If set to `password`, the component doesn't validate the inputted text. Placeholder text is anonymized
   */
  type?: 'text' | 'password';
}

interface ConfirmationDialogChildrenProps {
  handleOpen: () => void;
  handleClose: () => void;
}

export type ConfirmationDialogVariant =
  'error' | 'warning' | 'success' | 'info';

interface ConfirmationDialogProps {
  /**
   * Is the dialog open?
   */
  open?: boolean;
  /**
   * Children render function <Field name>{props => ...}</Field>)
   */
  children: (props: ConfirmationDialogChildrenProps) => React.ReactNode;
  /**
   * Callback that fires when the confirm delete button is clicked
   */
  onConfirm?: (answer: string) => Promise<void>;
  /**
   * Callback that fires when the cancel delete button is clicked
   */
  onCancel?: () => void;
  /**
   * Callback that fires when the dialog is closed
   */
  onClose?: () => void;
  /**
   * Title of the modal
   */
  title: string;
  /**
   * Content of the modal
   */
  content?: React.ReactNode;
  /**
   * Text of the delete confirmation button
   */
  confirmText?: string;
  /**
   * Text of the delete cancel button
   */
  cancelText?: string;
  /**
   * Show the confirm button?
   */
  showConfirm?: boolean;
  /**
   * Show the cancel button?
   */
  showCancel?: boolean;
  /**
   * Challenge to complete before confirm button is enabled
   */
  challenge?: ConfirmationChallenge;
  /**
   * Dialog variant. Determines palette and icons
   */
  variant?: ConfirmationDialogVariant;
  /**
   * Does the dialog close on backdrop click?
   */
  persistent?: boolean;
}

const ConfirmationDialog = ({
  open: initOpen = false,
  children,
  onCancel,
  onClose,
  challenge,
  title,
  content,
  variant = 'error',
  showConfirm = true,
  showCancel = true,
  persistent = false,
  ...props
}: ConfirmationDialogProps) => {
  const { formatMessage } = useIntl();
  const {
    cancelText = formatMessage(messages.cancel),
    confirmText = formatMessage(messages.confirm),
    onConfirm,
  } = props;
  const [open, setOpen] = React.useState(initOpen);
  const [loading, setLoading] = React.useState(false);
  const [answer, setAnswer] = React.useState('');

  const confirmDisabled = Boolean(
    loading ||
    (challenge && answer !== challenge.phrase && challenge.type === 'text')
  );

  React.useEffect(() => {
    if (!open) {
      setAnswer('');
    }
  }, [open]);

  React.useEffect(() => {
    setOpen(initOpen);
  }, [initOpen]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  const handleCancel: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onCancel?.();
    handleClose();
  };

  const handleConfirm: React.MouseEventHandler<HTMLButtonElement> = async (
    event
  ) => {
    if (confirmDisabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setLoading(true);
    await onConfirm?.(answer);
    setLoading(false);
    handleClose();
  };

  const handleTextChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setAnswer(value);
  };

  const placeholder =
    challenge?.type === 'text' ? challenge.phrase : '••••••••••';

  return (
    <>
      <Dialog
        disableScrollLock
        onClick={(e) => {
          if (persistent) {
            e.stopPropagation();
          }
        }}
        onClose={handleClose}
        open={open}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {content && <DialogContentText>{content}</DialogContentText>}
          {challenge ? (
            <DialogContentText>{challenge.description}</DialogContentText>
          ) : null}
          {challenge && (
            <TextInputBox
              onChange={handleTextChange}
              placeholder={placeholder}
              type={challenge.type}
              value={answer}
            />
          )}
        </DialogContent>
        <DialogActions>
          {showCancel && (
            <Button
              color={variant}
              disabled={loading}
              onClick={handleCancel}
              type="button"
              variant="outlined"
            >
              {cancelText}
            </Button>
          )}
          {showConfirm && (
            <Button
              color={variant}
              disabled={confirmDisabled}
              onClick={handleConfirm}
              type="button"
            >
              {confirmText}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {children({
        handleOpen,
        handleClose,
      })}
    </>
  );
};

export default ConfirmationDialog;
